import Swal from 'sweetalert2';

export class PartnerApiError extends Error {
  constructor(message, { fieldErrors = {}, status = null } = {}) {
    super(message);
    this.name = 'PartnerApiError';
    this.fieldErrors = fieldErrors;
    this.status = status;
  }
}

export const PARTNER_FIELD_LABELS = {
  partnerCompanyId: 'Partner company',
  partnerName: 'Partner name',
  partnerEmail: 'Partner email',
  companyName: 'Partner company',
  csn: 'Customer Support Number (CSN)',
  customerName: 'Customer name',
  projectLeadName: 'Partner Project lead name',
  projectLeadEmail: 'Partner Project lead email',
  productsInScope: 'Products in scope',
  engagementType: 'Engagement type',
  deploymentModel: 'Deployment model',
  projectStatus: 'Project status',
  estimatedStartDate: 'Estimated start date',
  estimatedGoliveDate: 'Estimated go-live date',
  actualStartDate: 'Actual start date',
  actualGoliveDate: 'Actual go-live date',
  completedDate: 'Completed date',
  projectDescription: 'Project description',
  salesforceOpportunityId: 'Salesforce Opportunity ID',
  competitiveContext: 'Competitive context',
  additionalNotes: 'Additional notes',
  actualCustomerName: 'Actual customer name',
  actualCustomerCsn: 'Actual customer CSN',
};

const PARTNER_API_FIELD_ALIASES = {
  partner_company_id: 'partnerCompanyId',
  partner_email: 'partnerEmail',
  partner_name: 'partnerName',
  company_name: 'companyName',
  customer_name: 'customerName',
  project_lead_name: 'projectLeadName',
  project_lead_email: 'projectLeadEmail',
  products_in_scope: 'productsInScope',
  engagement_type: 'engagementType',
  deployment_model: 'deploymentModel',
  project_status: 'projectStatus',
  estimated_start_date: 'estimatedStartDate',
  estimated_golive_date: 'estimatedGoliveDate',
  estimated_go_live_date: 'estimatedGoliveDate',
  actual_start_date: 'actualStartDate',
  actual_golive_date: 'actualGoliveDate',
  actual_go_live_date: 'actualGoliveDate',
  completed_date: 'completedDate',
  project_description: 'projectDescription',
  salesforce_opportunity_id: 'salesforceOpportunityId',
  competitive_context: 'competitiveContext',
  additional_notes: 'additionalNotes',
  actual_customer_name: 'actualCustomerName',
  actual_customer_csn: 'actualCustomerCsn',
};

export const PARTNER_ON_BEHALF_FIELD_KEYS = new Set([
  'partnerCompanyId',
  'companyName',
  'csn',
  'partnerName',
  'partnerEmail',
]);

const normalizePartnerFieldKey = (field) => (
  PARTNER_API_FIELD_ALIASES[field] || field
);

const getFieldLabel = (fieldName) => (
  PARTNER_FIELD_LABELS[fieldName] || fieldName.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase())
);

export const normalizePartnerFieldErrors = (fieldErrors = {}) => {
  if (!fieldErrors || typeof fieldErrors !== 'object') {
    return {};
  }

  return Object.entries(fieldErrors).reduce((accumulator, [field, message]) => {
    if (message) {
      accumulator[normalizePartnerFieldKey(field)] = String(message);
    }
    return accumulator;
  }, {});
};

export const splitSubmitFieldErrors = (fieldErrors = {}, { onBehalfMode = false } = {}) => {
  const normalized = normalizePartnerFieldErrors(fieldErrors);

  if (!onBehalfMode) {
    return { formErrors: normalized, onBehalfErrors: {} };
  }

  const formErrors = {};
  const onBehalfErrors = {};

  Object.entries(normalized).forEach(([field, message]) => {
    if (PARTNER_ON_BEHALF_FIELD_KEYS.has(field)) {
      if (field === 'companyName') {
        onBehalfErrors.partnerCompanyId = onBehalfErrors.partnerCompanyId || message;
      } else {
        onBehalfErrors[field] = message;
      }
      return;
    }

    formErrors[field] = message;
  });

  return { formErrors, onBehalfErrors };
};

export const getFirstFieldErrorMessage = (fieldErrors = {}) => {
  const normalized = normalizePartnerFieldErrors(fieldErrors);
  const entries = Object.entries(normalized);
  if (!entries.length) {
    return '';
  }

  const [field, message] = entries[0];
  return `${getFieldLabel(field)}: ${message}`;
};

export const scrollToFirstPartnerFormError = () => {
  requestAnimationFrame(() => {
    const firstInvalid = document.querySelector(
      '.partner-on-behalf-card .is-invalid, .partner-form .is-invalid, .partner-products-grid.has-error',
    );
    firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  });
};

export const getPartnerApiErrorMessage = (error, fallback = 'Something went wrong. Please try again.') => {
  if (!error) {
    return fallback;
  }

  if (error instanceof PartnerApiError) {
    const fieldMessages = Object.values(error.fieldErrors || {});
    if (fieldMessages.length > 0) {
      return fieldMessages[0];
    }
    return error.message || fallback;
  }

  return error.message || fallback;
};

export const buildPartnerValidationErrorHtml = (fieldErrors = {}) => {
  const entries = Object.entries(normalizePartnerFieldErrors(fieldErrors));
  if (!entries.length) {
    return '<p>Please review the form and try again.</p>';
  }

  const items = entries.map(([field, message]) => (
    `<li><strong>${getFieldLabel(field)}:</strong> ${message}</li>`
  )).join('');

  return `<ul class="partner-api-error-list">${items}</ul>`;
};

export const showPartnerValidationErrorPopup = async ({
  title = 'Please fix the following',
  fieldErrors = {},
}) => {
  const normalizedErrors = normalizePartnerFieldErrors(fieldErrors);
  if (!Object.keys(normalizedErrors).length) {
    return;
  }

  await Swal.fire({
    icon: 'error',
    title,
    html: buildPartnerValidationErrorHtml(normalizedErrors),
    confirmButtonText: 'OK',
    confirmButtonColor: '#052140',
    customClass: {
      title: 'swal-title-custom',
      htmlContainer: 'swal-text-custom',
    },
  });
};

export const showPartnerProjectUpdatedPopup = async () => {
  await Swal.fire({
    icon: 'success',
    title: 'Project Updated',
    text: 'Your project details have been saved successfully.',
    confirmButtonText: 'OK',
    confirmButtonColor: '#052140',
    customClass: {
      title: 'swal-title-custom',
      text: 'swal-text-custom',
    },
  });
};

export const showPartnerProjectSubmittedPopup = async () => {
  await Swal.fire({
    icon: 'success',
    title: 'Project Submitted',
    text: 'Your project has been submitted successfully.',
    confirmButtonText: 'OK',
    confirmButtonColor: '#052140',
    customClass: {
      title: 'swal-title-custom',
      text: 'swal-text-custom',
    },
  });
};

export const showPartnerActionErrorPopup = async (message, { isConflict = false } = {}) => {
  await Swal.fire({
    icon: isConflict ? 'warning' : 'error',
    title: isConflict ? 'Action not allowed' : 'Unable to complete action',
    text: message,
    confirmButtonText: 'OK',
    confirmButtonColor: '#052140',
    customClass: {
      title: 'swal-title-custom',
      text: 'swal-text-custom',
    },
  });
};
